import React, { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { BookOpen, ArrowRight, ArrowLeft } from 'lucide-react';
import { getRecipe, recipePath } from '../../data/guide/recipes';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { WhereToGet } from '../../components/world/WhereToGet';
import { HowToCraft, UsedToCraft } from '../../components/world/ItemCrafting';
import { skillKindOf, skillNameOf } from '../../utils/skillItems';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { entryPathById, getEntry } from '../../utils/guideIndex';
import { itemIdFromKey, itemName, itemPath, nameKey } from '../../utils/worldFormat';
import { derived } from '../../utils/worldQueries';
import { BoxContents } from '../../components/world/BagSourceList';
import { bagsOpenedBy } from '../../utils/bagSources';
import { EXPORT_DATE_LABEL } from '../../data/world/package';
import { DataStatus } from '../../components/world/DataStatus';
import { ItemThumb } from '../../components/world/ItemSprite';
import type { WorldData } from '../../types/world';
export function ItemDetail() {
  const {
    itemKey
  } = useParams();
  useHashScroll();
  return <WorldGate data-id="element-2512" data-mp="kpcsqwyzieza">
      {w => {
      const id = itemIdFromKey(itemKey ?? '');
      if (!w.items.has(id) && !w.variants.has(id)) {
        return <div className="rounded-2xl border border-white/10 p-6" data-id="element-2513" data-mp="b0l6n5gluowa">
              <p className="text-white" data-id="element-2514" data-mp="ayrujan83cgw">No encontramos ese ítem en los archivos.</p>
              <Link to="/guia/items" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-2515" data-mp="61hyhc5bw2k3">Volver a Ítems y drops</Link>
            </div>;
      }
      return <ItemView w={w} id={id} data-id="element-2516" data-mp="yq70dde7kbww" />;
    }}
    </WorldGate>;
}
function ItemView({
  w,
  id
}: {
  w: WorldData;
  id: string;
}) {
  const variant = w.variants.get(id);
  const baseId = variant?.itemId ?? id;
  const item = w.items.get(baseId);
  const name = itemName(w, id);
  const childVariants = (w.idx.variantsByItem.get(id) ?? []).filter(v => v.id !== id);
  const guideEntryId = derived(w).guideItemByName.get(nameKey(name));
  const guideEntry = guideEntryId ? getEntry(guideEntryId) : undefined;
  const skill = item ? skillNameOf(item) : undefined;
  const [params] = useSearchParams();
  const fromRecipe = getRecipe(params.get('desde') ?? undefined);
  useEffect(() => {
    document.title = `${name} · Ítems · Guía MU Vanguard`;
  }, [name]);
  return <div data-id="element-2517" data-mp="20byjd66vnoe6">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Ítems y drops',
      to: '/guia/items'
    }, {
      label: name
    }]} eyebrow={variant && variant.id !== variant.itemId ? 'Variante de ítem' : 'Ítem'} title={name} media={<ItemThumb id={id} name={name} size="hero" placeholder lazy={false} data-id="element-2519" data-mp="3hvd8ne6v4im" />} data-id="element-2518" data-mp="258oz1sershz2">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2520" data-mp="1nze35btk78x7">
          {item?.requiredLevel !== undefined && <span className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-gray-200" data-id="element-2521" data-mp="1j0jie4c17aag">Nivel requerido (configuración): {item.requiredLevel}</span>}
          {variant?.level !== undefined && <span className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-gray-200" data-id="element-2522" data-mp="1syh7eu1ptb44">Nivel de variante: {variant.level}</span>}
          {fromRecipe && <Link to={recipePath(fromRecipe.id)} className="inline-flex h-10 items-center gap-2 rounded-full bg-[#D4AF37] px-4 text-sm font-semibold text-[#050b14] hover:bg-[#F3E5AB]" data-id="element-2523" data-mp="26o15vno9jm7m">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-2524" data-mp="2gnjlxt11ynue" />
              Volver a {fromRecipe.shortTitle}
            </Link>}
          <CopyLinkButton label="Copiar enlace" data-id="element-2525" data-mp="ma0vf35fw5il" />
        </div>
        {item && <div className="mt-3" data-id="element-2526" data-mp="mbvfygcstvm5">
            <DataStatus kind="catalog" data-id="element-2527" data-mp="hbh38l095xhs" />
          </div>}
        {skill && <p className="mt-3 text-[15px] text-gray-200" data-id="element-2528" data-mp="34jn87f26utup">
            {skillKindOf(item!)} · enseña el poder <span className="font-semibold text-[#F3E5AB]" data-id="element-2529" data-mp="39i7xwkjtdv4w">{skill}</span>
          </p>}
        {item?.availabilityNote && <p className="mt-3 text-sm text-gray-400" data-id="element-2530" data-mp="3ehb6gez06jhm">{item.availabilityNote}</p>}
        {variant?.nameContext && <p className="mt-3 text-[15px] text-gray-200" data-id="element-2531" data-mp="3ijftuvyuyjvt">{variant.nameContext}</p>}
        {variant?.nameSource && <details className="mt-2 text-xs text-gray-500" data-id="element-2532" data-mp="2lu6t9mprayur">
            <summary className="flex min-h-[32px] cursor-pointer list-none items-center hover:text-gray-300" data-id="element-2533" data-mp="2qrqj7jvth02j">Sobre este nombre</summary>
            <p data-id="element-2534" data-mp="2ussbthv8advj">
              Nombre de presentación:{' '}
              {variant.nameSourceUrl ? <a href={variant.nameSourceUrl} target="_blank" rel="noreferrer" className="underline decoration-white/20 underline-offset-4 hover:text-gray-300" data-id="element-2535" data-mp="2zl3j55sat1y4">{variant.nameSource}</a> : variant.nameSource}
              . Pendiente del nombre exacto en el cliente de Vanguard.
              {variant.exportedName && ` En los archivos exportados figura como “${variant.exportedName}”, un nombre generado automáticamente.`}
            </p>
          </details>}
      </WorldPageHeader>

      {guideEntry && <Link to={entryPathById(guideEntry.id)} className="group mb-8 flex items-center gap-3 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] p-4 transition-colors duration-150 hover:bg-[#D4AF37]/10" data-id="element-2536" data-mp="1ll8ivg4jj043">
          <BookOpen className="h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-2537" data-mp="1bunl1uwoxzm8" />
          <span className="flex-1" data-id="element-2538" data-mp="16vb0xhx7f6nc">
            <span className="block font-semibold text-white" data-id="element-2539" data-mp="16wcdjqtvc176">Ficha de la guía: {guideEntry.title}</span>
            <span className="block text-sm text-gray-400" data-id="element-2540" data-mp="12vsicssuv7no">Incluye las invasiones aprobadas que lo entregan.</span>
          </span>
          <ArrowRight className="h-4 w-4 text-[#D4AF37] transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-2541" data-mp="xwz24sm6p2jt" />
        </Link>}

      <div className="space-y-10" data-id="element-2542" data-mp="j6ur1xxgvygr">
        <WhereToGet w={w} id={id} name={name} data-id="element-2543" data-mp="o7ipv5rxtjlj" />

        <HowToCraft id={id} name={name} data-id="element-2544" data-mp="o8a8lg0frmue" />

        <UsedToCraft id={id} name={name} data-id="element-2545" data-mp="9a9hr0ef0174" />

        <BoxContents w={w} bags={bagsOpenedBy(w, id)} data-id="element-2546" data-mp="9bb4w38pge57" />

        {(w.itemClasses.get(baseId) ?? []).length > 0 && <WorldSection id="clases" title="Clases que pueden usarlo" data-id="element-2547" data-mp="e9us06z1bvz5">
            <ul className="flex flex-wrap gap-2" data-id="element-2548" data-mp="11ynhplp2d7lb">
              {Array.from(new Set(w.itemClasses.get(baseId)!)).map(c => <li key={c} className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-gray-200" data-id="element-2549" data-mp="16xgybmzndepp">{w.classNames.get(c) ?? `Clase ${c}`}</li>)}
            </ul>
            <p className="mt-2 text-xs text-gray-500" data-id="element-2550" data-mp="x6w0w2vpmg8d">Según la configuración exportada el {EXPORT_DATE_LABEL}.</p>
          </WorldSection>}

        {childVariants.length > 0 && <WorldSection id="variantes" title="Variantes" count={childVariants.length} data-id="element-2551" data-mp="1ppjgj2rxt322">
            <ul className="flex flex-wrap gap-2" data-id="element-2552" data-mp="3ae52ktp8pafw">
              {childVariants.map(v => <li key={v.id} data-id="element-2553" data-mp="35gbyq7bessis">
                  <Link to={itemPath(v.id)} className="inline-flex h-10 items-center rounded-full border border-white/15 px-4 text-sm font-semibold text-gray-200 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-2554" data-mp="35fam3yeqvxyy">
                    {v.name}
                  </Link>
                </li>)}
            </ul>
          </WorldSection>}

        {variant && variant.id !== variant.itemId && <p className="text-sm text-gray-400" data-id="element-2555" data-mp="2miiw764p6klx">
            Ítem base: <Link to={itemPath(variant.itemId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-2556" data-mp="30h8tomvfimat">{itemName(w, variant.itemId)}</Link>
          </p>}

        {item && <Disclosure title="Datos de catálogo" data-id="element-2557" data-mp="fpgr5wh1pjgk">
            <StatList rows={[{
          label: 'Nivel requerido (configuración)',
          value: item.requiredLevel
        }, {
          label: 'Energía requerida (configuración)',
          value: item.requiredEnergy
        }, {
          label: 'Nivel de drop (catálogo)',
          value: item.dropLevel
        }, {
          label: 'Daño mínimo',
          value: item.damageMin
        }, {
          label: 'Daño máximo',
          value: item.damageMax
        }, {
          label: 'Defensa',
          value: item.defense
        }, {
          label: 'Velocidad de ataque',
          value: item.attackSpeed
        }, {
          label: 'Tamaño en inventario',
          value: item.width !== undefined && item.height !== undefined ? `${item.width} × ${item.height}` : undefined
        }, {
          label: 'Drop natural habilitado',
          value: item.naturalDropEnabled === undefined ? undefined : item.naturalDropEnabled ? 'Sí' : 'No'
        }, {
          label: 'ID',
          value: item.id
        }]} data-id="element-2558" data-mp="fscp3ksw8850" />
          </Disclosure>}
      </div>
    </div>;
}